import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  CheckCircle2,
  Save
} from 'lucide-react';
import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../core/firebase/firebase';

export const TEACHER_ONBOARDING_VERSION = 1;

const MARITAL_STATUSES = [
  ['single', 'Célibataire'],
  ['married', 'Marié(e)'],
  ['divorced', 'Divorcé(e)'],
  ['widowed', 'Veuf / veuve'],
  ['other', 'Autre']
];

const clean = (value) => String(value ?? '').trim();

function parseBirthDate(value = '') {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return match
    ? { year: match[1], month: match[2], day: match[3] }
    : { year: '', month: '', day: '' };
}

function buildBirthDate(day, month, year) {
  if (!day || !month || !year) return '';
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function validBirthDate(day, month, year) {
  const iso = buildBirthDate(day, month, year);
  if (!iso) return false;
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  const currentYear = new Date().getFullYear();
  return (
    date.getUTCFullYear() === y &&
    date.getUTCMonth() === m - 1 &&
    date.getUTCDate() === d &&
    y >= 1940 &&
    y <= currentYear - 16
  );
}

function displayBirthDate(form) {
  if (!form.birthDay || !form.birthMonth || !form.birthYear) return '—';
  return `${String(form.birthDay).padStart(2, '0')}/${String(form.birthMonth).padStart(2, '0')}/${form.birthYear}`;
}

function splitName(fullname = '') {
  const parts = clean(fullname).split(/\s+/).filter(Boolean);
  if (parts.length === 0) return { firstName: '', lastName: '' };
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

function buildInitialForm(profile = {}) {
  const notice = profile.teacherNotice || {};
  const fallbackName = splitName(profile.fullname || profile.fullName || profile.displayName || '');
  const birth = parseBirthDate(notice.birthDate || '');

  return {
    firstName: notice.firstName || fallbackName.firstName,
    lastName: notice.lastName || fallbackName.lastName,
    gender: notice.gender || '',
    cin: notice.cin || '',
    birthDay: birth.day,
    birthMonth: birth.month,
    birthYear: birth.year,
    birthPlace: notice.birthPlace || '',
    address: notice.address || '',
    phone: notice.phone || profile.phone || '',
    personalEmail: notice.personalEmail || profile.personalEmail || '',

    maritalStatus: notice.maritalStatus || '',
    maritalStatusOther: notice.maritalStatusOther || '',
    spouseFullName: notice.spouseFullName || '',
    spouseProfession: notice.spouseProfession || '',
    childrenCount: notice.childrenCount ?? 0,

    diploma: notice.diploma || '',
    studyField: notice.studyField || '',
    institution: notice.institution || '',
    graduationYear: notice.graduationYear || '',
    cnssNumber: notice.cnssNumber || '',

    bankName: notice.bankName || '',
    rib: notice.rib || '',

    confirmationAccepted: notice.confirmationAccepted === true
  };
}

function TextField({ label, value, onChange, type = 'text', required = false, placeholder = '', inputMode, min, max }) {
  return (
    <label>
      {label}
      <input
        type={type}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        placeholder={placeholder}
        inputMode={inputMode}
        min={min}
        max={max}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, required = false, children }) {
  return (
    <label>
      {label}
      <select value={value || ''} onChange={(event) => onChange(event.target.value)} required={required}>
        {children}
      </select>
    </label>
  );
}

function BirthDateFields({ form, patch }) {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear - 16; year >= 1940; year -= 1) years.push(year);

  return (
    <div className="teacher-onboarding-field-span">
      <span className="teacher-onboarding-field-label">Date de naissance</span>
      <div className="teacher-birthdate-grid">
        <label>
          Jour
          <select value={form.birthDay} onChange={(event) => patch('birthDay', event.target.value)} required>
            <option value="">JJ</option>
            {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => (
              <option key={day} value={String(day).padStart(2, '0')}>{String(day).padStart(2, '0')}</option>
            ))}
          </select>
        </label>
        <label>
          Mois
          <select value={form.birthMonth} onChange={(event) => patch('birthMonth', event.target.value)} required>
            <option value="">MM</option>
            {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
              <option key={month} value={String(month).padStart(2, '0')}>{String(month).padStart(2, '0')}</option>
            ))}
          </select>
        </label>
        <label>
          Année
          <select value={form.birthYear} onChange={(event) => patch('birthYear', event.target.value)} required>
            <option value="">AAAA</option>
            {years.map((year) => <option key={year} value={String(year)}>{year}</option>)}
          </select>
        </label>
      </div>
    </div>
  );
}

function maritalLabel(value, other) {
  if (value === 'other') return clean(other) || 'Autre';
  return MARITAL_STATUSES.find(([key]) => key === value)?.[1] || '—';
}

export default function TeacherProfileForm({ profile, user, onSaved, mode = 'onboarding', onCancel }) {
  const [form, setForm] = useState(() => buildInitialForm(profile));
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const isEdit = mode === 'edit';

  const steps = useMemo(
    () => ['Identité', 'Situation familiale', 'Parcours', 'Banque & CNSS', 'Vérification'],
    []
  );

  const patch = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  function validateStep(index) {
    setError('');

    if (index === 0) {
      if (!clean(form.firstName) || !clean(form.lastName) || !form.gender || !clean(form.cin)) {
        setError('Complétez le nom, le prénom, le genre et le numéro de CIN.');
        return false;
      }
      if (!validBirthDate(form.birthDay, form.birthMonth, form.birthYear)) {
        setError('Vérifiez la date de naissance.');
        return false;
      }
      if (!clean(form.birthPlace) || !clean(form.address) || !clean(form.phone)) {
        setError('Complétez le lieu de naissance, l’adresse complète et le téléphone.');
        return false;
      }
    }

    if (index === 1) {
      if (!form.maritalStatus) {
        setError('Indiquez votre situation familiale.');
        return false;
      }
      if (form.maritalStatus === 'other' && !clean(form.maritalStatusOther)) {
        setError('Précisez votre situation familiale.');
        return false;
      }
      if (form.maritalStatus === 'married' && (!clean(form.spouseFullName) || !clean(form.spouseProfession))) {
        setError('Pour une personne mariée, renseignez le nom du conjoint et sa profession.');
        return false;
      }
      const count = Number(form.childrenCount);
      if (!Number.isInteger(count) || count < 0 || count > 20) {
        setError('Indiquez un nombre d’enfants valide.');
        return false;
      }
    }

    if (index === 2) {
      if (!clean(form.diploma) || !clean(form.studyField)) {
        setError('Renseignez votre diplôme et votre spécialité / domaine d’études.');
        return false;
      }
    }

    if (index === 3) {
      if (!clean(form.cnssNumber) || !clean(form.bankName) || !clean(form.rib)) {
        setError('Renseignez le numéro CNSS, la banque et le RIB.');
        return false;
      }
    }

    if (index === steps.length - 1 && !form.confirmationAccepted) {
      setError('Confirmez que les informations communiquées sont exactes et à jour.');
      return false;
    }

    return true;
  }

  function next() {
    if (!validateStep(step)) return;
    setStep((current) => Math.min(current + 1, steps.length - 1));
  }

  async function save() {
    if (!validateStep(steps.length - 1) || !user?.uid || saving) return;

    setSaving(true);
    setError('');

    const notice = {
      schemaVersion: TEACHER_ONBOARDING_VERSION,
      firstName: clean(form.firstName),
      lastName: clean(form.lastName),
      gender: form.gender,
      cin: clean(form.cin),
      birthDate: buildBirthDate(form.birthDay, form.birthMonth, form.birthYear),
      birthPlace: clean(form.birthPlace),
      address: clean(form.address),
      phone: clean(form.phone),
      personalEmail: clean(form.personalEmail) || null,

      maritalStatus: form.maritalStatus,
      maritalStatusOther: form.maritalStatus === 'other' ? clean(form.maritalStatusOther) : null,
      spouseFullName: form.maritalStatus === 'married' ? clean(form.spouseFullName) : null,
      spouseProfession: form.maritalStatus === 'married' ? clean(form.spouseProfession) : null,
      childrenCount: Number(form.childrenCount),

      diploma: clean(form.diploma),
      studyField: clean(form.studyField),
      institution: clean(form.institution) || null,
      graduationYear: clean(form.graduationYear) || null,
      cnssNumber: clean(form.cnssNumber),

      bankName: clean(form.bankName),
      rib: clean(form.rib),
      confirmationAccepted: true,
      completedAt: profile?.teacherNotice?.completedAt || serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    try {
      const userUpdate = {
        teacherNotice: notice,
        teacherOnboardingVersion: TEACHER_ONBOARDING_VERSION,
        updatedAt: serverTimestamp()
      };

      if (!isEdit) {
        userUpdate.teacherOnboardingCompletedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'users', user.uid), userUpdate);

      onSaved?.(notice);
    } catch (err) {
      console.error('TeacherProfileForm — save:', err);
      setError('Impossible d’enregistrer la notice individuelle. Réessayez dans quelques instants.');
    } finally {
      setSaving(false);
    }
  }

  const renderIdentity = () => (
    <div className="teacher-onboarding-stack">
      <article className="teacher-onboarding-subcard">
        <div className="teacher-onboarding-grid two">
          <TextField label="Prénom" value={form.firstName} onChange={(value) => patch('firstName', value)} required />
          <TextField label="Nom" value={form.lastName} onChange={(value) => patch('lastName', value)} required />
          <SelectField label="Genre" value={form.gender} onChange={(value) => patch('gender', value)} required>
            <option value="">Choisir</option>
            <option value="female">Féminin</option>
            <option value="male">Masculin</option>
          </SelectField>
          <TextField label="CIN" value={form.cin} onChange={(value) => patch('cin', value)} required inputMode="numeric" />
          <BirthDateFields form={form} patch={patch} />
          <TextField label="Lieu de naissance" value={form.birthPlace} onChange={(value) => patch('birthPlace', value)} required />
          <div className="teacher-onboarding-field-span">
            <TextField label="Adresse complète et détaillée" value={form.address} onChange={(value) => patch('address', value)} required placeholder="Rue, numéro, quartier, ville…" />
          </div>
          <TextField label="Téléphone" value={form.phone} onChange={(value) => patch('phone', value)} required inputMode="tel" />
          <TextField label="Email personnel" type="email" value={form.personalEmail} onChange={(value) => patch('personalEmail', value)} placeholder="Facultatif" />
        </div>
      </article>
    </div>
  );

  const renderFamily = () => (
    <div className="teacher-onboarding-stack">
      <article className="teacher-onboarding-subcard">
        <div className="teacher-onboarding-grid two">
          <SelectField label="Situation familiale" value={form.maritalStatus} onChange={(value) => patch('maritalStatus', value)} required>
            <option value="">Choisir</option>
            {MARITAL_STATUSES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </SelectField>
          <TextField label="Nombre d’enfants" type="number" min="0" max="20" value={form.childrenCount} onChange={(value) => patch('childrenCount', value)} required />
          {form.maritalStatus === 'other' && (
            <div className="teacher-onboarding-field-span">
              <TextField label="Préciser la situation familiale" value={form.maritalStatusOther} onChange={(value) => patch('maritalStatusOther', value)} required />
            </div>
          )}
        </div>
      </article>

      {form.maritalStatus === 'married' && (
        <article className="teacher-onboarding-subcard">
          <div className="teacher-onboarding-subcard-title">
            <strong>Informations sur le conjoint</strong>
            <span>Personne mariée</span>
          </div>
          <div className="teacher-onboarding-grid two">
            <TextField label="Nom et prénom du conjoint" value={form.spouseFullName} onChange={(value) => patch('spouseFullName', value)} required />
            <TextField label="Fonction / profession du conjoint" value={form.spouseProfession} onChange={(value) => patch('spouseProfession', value)} required />
          </div>
        </article>
      )}
    </div>
  );

  const renderProfessional = () => (
    <div className="teacher-onboarding-stack">
      <article className="teacher-onboarding-subcard">
        <div className="teacher-onboarding-grid two">
          <TextField label="Diplôme obtenu" value={form.diploma} onChange={(value) => patch('diploma', value)} required placeholder="Ex. Licence, Master…" />
          <TextField label="Spécialité / domaine d’études" value={form.studyField} onChange={(value) => patch('studyField', value)} required />
          <TextField label="Établissement / université" value={form.institution} onChange={(value) => patch('institution', value)} placeholder="Facultatif" />
          <TextField label="Année d’obtention" value={form.graduationYear} onChange={(value) => patch('graduationYear', value)} inputMode="numeric" placeholder="Facultatif" />
        </div>
      </article>
    </div>
  );

  const renderBanking = () => (
    <div className="teacher-onboarding-stack">
      <article className="teacher-onboarding-subcard">
        <div className="teacher-onboarding-grid two">
          <TextField label="Numéro CNSS" value={form.cnssNumber} onChange={(value) => patch('cnssNumber', value)} required />
          <TextField label="Banque" value={form.bankName} onChange={(value) => patch('bankName', value)} required />
          <div className="teacher-onboarding-field-span">
            <TextField label="RIB bancaire" value={form.rib} onChange={(value) => patch('rib', value)} required inputMode="numeric" placeholder="Saisissez le RIB complet" />
          </div>
        </div>
      </article>
      <p className="teacher-onboarding-helper">Ces informations sont utilisées uniquement pour la gestion administrative de votre dossier.</p>
    </div>
  );

  const renderReview = () => (
    <div className="teacher-review-grid">
      <article><span>Identité</span><strong>{form.firstName} {form.lastName}</strong><small>{form.gender === 'female' ? 'Féminin' : 'Masculin'} · CIN {form.cin}</small></article>
      <article><span>Naissance</span><strong>{displayBirthDate(form)}</strong><small>{form.birthPlace || '—'}</small></article>
      <article><span>Situation familiale</span><strong>{maritalLabel(form.maritalStatus, form.maritalStatusOther)}</strong><small>{Number(form.childrenCount) || 0} enfant(s)</small></article>
      {form.maritalStatus === 'married' && <article><span>Conjoint</span><strong>{form.spouseFullName || '—'}</strong><small>{form.spouseProfession || '—'}</small></article>}
      <article><span>Diplôme</span><strong>{form.diploma || '—'}</strong><small>{form.studyField || '—'}{form.institution ? ` · ${form.institution}` : ''}</small></article>
      <article><span>CNSS</span><strong>{form.cnssNumber || '—'}</strong><small>Dossier administratif</small></article>
      <article><span>Coordonnées bancaires</span><strong>{form.bankName || '—'}</strong><small>RIB : {form.rib || '—'}</small></article>
      <article><span>Contact</span><strong>{form.phone || '—'}</strong><small>{form.personalEmail || 'Email personnel non renseigné'}</small></article>
      <label className="teacher-confirmation-row">
        <input type="checkbox" checked={form.confirmationAccepted} onChange={(event) => patch('confirmationAccepted', event.target.checked)} />
        <span>Je confirme que les informations communiquées sont exactes et à jour.</span>
      </label>
    </div>
  );

  const renderStep = () => {
    if (step === 0) return renderIdentity();
    if (step === 1) return renderFamily();
    if (step === 2) return renderProfessional();
    if (step === 3) return renderBanking();
    return renderReview();
  };

  const descriptions = [
    'Renseignez vos informations personnelles et vos coordonnées.',
    'Indiquez votre situation familiale. Les informations sur le conjoint sont demandées uniquement si vous êtes marié(e).',
    'Renseignez votre diplôme et votre parcours d’études.',
    'Ajoutez les informations nécessaires à votre dossier administratif et à la paie.',
    'Relisez votre notice individuelle avant de l’enregistrer.'
  ];

  return (
    <main className={isEdit ? 'teacher-profile-edit-screen-v1' : 'teacher-onboarding-screen'}>
      <section className={`teacher-onboarding-shell ${isEdit ? 'teacher-profile-edit-shell-v1' : ''}`}>
        <header className="teacher-onboarding-header">
          <div className="teacher-onboarding-logo">
            <img src="/logo.png" alt="Le Défi" />
            <div>
              <p className="eyebrow">{isEdit ? 'MON PROFIL' : 'PREMIÈRE CONNEXION'}</p>
              <h1>{isEdit ? 'Modifier ma notice individuelle' : 'Complétez votre notice individuelle'}</h1>
              <p>{isEdit ? 'Mettez à jour les informations de votre dossier professionnel.' : 'Ces informations permettent à l’administration de constituer et tenir à jour votre dossier professionnel.'}</p>
            </div>
          </div>
          <div className="teacher-onboarding-badge"><BadgeCheck size={18} /><span>{isEdit ? 'Informations enregistrées' : 'Étape obligatoire'}</span></div>
        </header>

        <div className="teacher-onboarding-progress" aria-label="Progression de la notice individuelle">
          {steps.map((label, index) => (
            <button
              key={label}
              type="button"
              className={`${index === step ? 'active' : ''} ${index < step ? 'done' : ''}`}
              onClick={() => (isEdit || index <= step) && setStep(index)}
            >
              <span>{index < step ? <CheckCircle2 size={15} /> : index + 1}</span>
              <b>{label}</b>
            </button>
          ))}
        </div>

        <div className="teacher-onboarding-body">
          <div className="teacher-onboarding-title">
            <span>Étape {step + 1} sur {steps.length}</span>
            <h2>{steps[step]}</h2>
            <p>{descriptions[step]}</p>
          </div>

          {error && <div className="alert alert-error teacher-onboarding-error">{error}</div>}
          {renderStep()}
        </div>

        <footer className="teacher-onboarding-footer">
          <div>{isEdit && onCancel && <button type="button" className="button button-secondary" onClick={onCancel}>Annuler</button>}</div>
          <div className="teacher-onboarding-actions">
            {step > 0 && (
              <button type="button" className="button button-secondary" onClick={() => { setError(''); setStep((current) => current - 1); }}>
                <ArrowLeft size={16} /> Précédent
              </button>
            )}
            {step < steps.length - 1 ? (
              <button type="button" className="button button-primary" onClick={next}>
                Continuer <ArrowRight size={16} />
              </button>
            ) : (
              <button type="button" className="button button-primary" onClick={save} disabled={saving}>
                <Save size={16} /> {saving ? 'Enregistrement…' : isEdit ? 'Enregistrer mes modifications' : 'Enregistrer et accéder à mon espace'}
              </button>
            )}
          </div>
        </footer>
      </section>
    </main>
  );
}
